/**
 * WebGPU capability detection.
 *
 * The vectorizer's optimization loop is WebGPU-only, so the app gates itself
 * behind this check at startup rather than failing later with a cryptic error.
 */

export type GpuUnsupportedReason = 'no-api' | 'no-adapter' | 'no-device' | 'error'

export type GpuSupport =
  | { supported: true; adapterInfo: GpuAdapterSummary }
  | { supported: false; reason: GpuUnsupportedReason; detail?: string }

export interface GpuAdapterSummary {
  vendor: string
  architecture: string
  description: string
  /** Largest square canvas the adapter can address, derived from binding limits. */
  maxTextureDimension: number
  maxBufferSize: number
  maxWorkgroupStorageSize: number
}

/** Minimal structural type so tests can supply a fake navigator. */
export interface GpuCapableNavigator {
  gpu?: {
    requestAdapter(options?: GPURequestAdapterOptions): Promise<GPUAdapter | null>
  }
}

/**
 * Probes for a usable WebGPU adapter.
 *
 * Presence of `navigator.gpu` alone is not sufficient: some browsers expose the
 * API but fail to return an adapter (blocklisted drivers, headless without a
 * software rasterizer), so an adapter request is part of the check.
 */
export async function detectWebGPU(
  nav: GpuCapableNavigator = globalThis.navigator as unknown as GpuCapableNavigator,
): Promise<GpuSupport> {
  if (!nav?.gpu) {
    return { supported: false, reason: 'no-api' }
  }

  try {
    const adapter = await nav.gpu.requestAdapter({ powerPreference: 'high-performance' })
    if (!adapter) {
      return { supported: false, reason: 'no-adapter' }
    }
    return { supported: true, adapterInfo: summarizeAdapter(adapter) }
  } catch (error) {
    return { supported: false, reason: 'error', detail: describeError(error) }
  }
}

export interface GpuContext {
  adapter: GPUAdapter
  device: GPUDevice
  info: GpuAdapterSummary
}

export class GpuUnavailableError extends Error {
  constructor(
    readonly reason: GpuUnsupportedReason,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'GpuUnavailableError'
  }
}

/**
 * Acquires a device for the optimizer.
 *
 * Requests the largest buffer and workgroup-storage limits the adapter offers,
 * because the splat renderer's tile lists scale with image size and the default
 * 128 MiB buffer cap is reached by a 1024 px canvas with dense strokes.
 */
export async function requestGpuDevice(
  nav: GpuCapableNavigator = globalThis.navigator as unknown as GpuCapableNavigator,
  onLost?: (info: GPUDeviceLostInfo) => void,
): Promise<GpuContext> {
  if (!nav?.gpu) {
    throw new GpuUnavailableError('no-api', describeUnsupported('no-api'))
  }

  let adapter: GPUAdapter | null
  try {
    adapter = await nav.gpu.requestAdapter({ powerPreference: 'high-performance' })
  } catch (cause) {
    throw new GpuUnavailableError('error', describeUnsupported('error'), { cause })
  }
  if (!adapter) {
    throw new GpuUnavailableError('no-adapter', describeUnsupported('no-adapter'))
  }

  const limits = adapter.limits
  let device: GPUDevice
  try {
    device = await adapter.requestDevice({
      requiredLimits: {
        maxBufferSize: limits.maxBufferSize,
        maxStorageBufferBindingSize: limits.maxStorageBufferBindingSize,
        maxComputeWorkgroupStorageSize: limits.maxComputeWorkgroupStorageSize,
        maxStorageBuffersPerShaderStage: Math.min(limits.maxStorageBuffersPerShaderStage, 10),
      },
    })
  } catch (cause) {
    throw new GpuUnavailableError('no-device', describeUnsupported('no-device'), { cause })
  }

  // A lost device cannot be revived; the caller surfaces a retry action.
  if (onLost) void device.lost.then(onLost)

  return { adapter, device, info: summarizeAdapter(adapter) }
}

export function summarizeAdapter(adapter: GPUAdapter): GpuAdapterSummary {
  // `adapter.info` is the current spec; older implementations exposed nothing.
  const info = (adapter as unknown as { info?: Partial<GPUAdapterInfo> }).info
  const limits = adapter.limits
  return {
    vendor: info?.vendor || 'unknown',
    architecture: info?.architecture || 'unknown',
    description: info?.description || '',
    maxTextureDimension: limits?.maxTextureDimension2D ?? 0,
    maxBufferSize: Number(limits?.maxBufferSize ?? 0),
    maxWorkgroupStorageSize: limits?.maxComputeWorkgroupStorageSize ?? 0,
  }
}

export function describeUnsupported(reason: GpuUnsupportedReason): string {
  switch (reason) {
    case 'no-api':
      return 'This browser does not expose the WebGPU API.'
    case 'no-adapter':
      return 'WebGPU is available but no compatible graphics adapter was found.'
    case 'no-device':
      return 'A graphics adapter was found but it refused to provide a device.'
    case 'error':
      return 'Initializing WebGPU failed unexpectedly.'
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}
